import { type FunctionComponent, type ReactNode, useRef } from 'react';

import { Link, WithTooltip } from '@dr.pogodin/react-utils';

import useVimeoVideoData from 'hooks/useVimeoVideoData';

import {
  ALLOWED_ASSET_EXT,
  type AssetT,
  formatByteSize,
  getVimeoVideoId,
  isAllowedFile,
} from 'utils';

import IconAudio from 'assets/images/icon-audio-16px.svg';
import IconDoc from 'assets/images/icon-doc-16px.svg';
import IconImage from 'assets/images/icon-image-16px.svg';
import IconInfo from 'assets/images/icon-info-16px.svg';

import IconTrash from 'assets/images/icon-trash-16px.svg';
import IconUpload from 'assets/images/icon-upload-24px.svg';

import IconVideo from 'assets/images/icon-video-16px-b.svg';

import './style.scss';

type PropsT = {
  assets: AssetT[];
  files: string[];
  localFiles: File[];
  removeAsset: (asset: AssetT) => void;
  removeFile: (url: string) => void;
  setLocalFiles: (files: File[]) => void;
  withoutUploadedFilesInfo?: boolean;
};

export const MediaFileUpload: FunctionComponent<PropsT> = ({
  assets,
  files,
  localFiles,
  removeAsset,
  removeFile,
  setLocalFiles,
  withoutUploadedFilesInfo,

  // eslint-disable-next-line complexity
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileNodes: ReactNode[] = [];

  const storageIds: string[] = [];
  const vimeoVideoIds: Record<string, string> = {};

  if (files.length) {
    for (const file of files) {
      const vimeoId = getVimeoVideoId(file, true);
      if (vimeoId) vimeoVideoIds[file] = vimeoId;
      else storageIds.push(file.replace(/\./g, '%2E'));
    }
  }

  const vimeoVideoData = useVimeoVideoData(Object.values(vimeoVideoIds));

  if (
    !withoutUploadedFilesInfo
    && (assets.length || files.length || localFiles.length)
  ) {
    fileNodes.push(<p key="header" styleName="header">Files uploaded</p>);

    for (const asset of assets) {
      let icon: ReactNode;
      let name;
      let size;

      switch (asset.kind) {
        case 'AUDIO':
          icon = <IconAudio />;
          ({ name, size } = asset.storageProperties);
          break;
        case 'DOCUMENT':
          icon = <IconDoc />;
          ({ name, size } = asset.storageProperties);
          break;
        case 'IMAGE':
          icon = <IconImage />;
          ({ name, size } = asset.storageProperties);
          break;
        case 'VIDEO':
          // TODO: This is what we want to do here, once we wire up
          // video meta data into the asset model:
          //  icon = <IconVideo />;
          //  const data = vimeoVideoData[vimeoId];
          //  if (data) {
          //    ({ name, size } = data);
          //  }
          throw Error('Not yet supported');
        default: throw Error('Unexpected asset kind');
      }

      fileNodes.push(
        <div key={asset.id} styleName="fileRecord">
          <div styleName="name">{icon} [{name}]</div>
          <div styleName="sizeAndTrash">
            {formatByteSize(size)}
            <IconTrash
              onClick={() => {
                removeAsset(asset);
              }}
              styleName="iconButton"
            />
          </div>
        </div>,
      );
    }

    for (let i = 0; i < files.length; ++i) {
      const file = files[i];
      if (!file) throw Error('Missing file');

      const vimeoId = vimeoVideoIds[file];

      let icon: ReactNode;
      let name = 'loading details...';
      let size = 0;
      if (vimeoId) {
        icon = <IconVideo />;
        const data = vimeoVideoData[vimeoId];
        if (data) {
          ({ name, size } = data);
        }
      } else throw Error('Internal error');

      fileNodes.push(
        <div key={`file-remote/${i}`} styleName="fileRecord">
          <div styleName="name">{icon} [{name}]</div>
          <div styleName="sizeAndTrash">
            {formatByteSize(size)}
            <IconTrash
              onClick={() => {
                removeFile(file);
              }}
              styleName="iconButton"
            />
          </div>
        </div>,
      );
    }

    for (let i = 0; i < localFiles.length; ++i) {
      const file = localFiles[i];
      if (!file) throw Error('Missing file');

      let icon: ReactNode;
      if (file.type.startsWith('audio/')) icon = <IconAudio />;
      else if (file.type.startsWith('image/')) icon = <IconImage />;
      else if (file.type.startsWith('video/')) icon = <IconVideo />;
      else icon = <IconDoc />;

      fileNodes.push(
        <div key={`file-local/${i}`} styleName="fileRecord">
          <div styleName="name">{icon} [{file.name}]</div>
          <div styleName="sizeAndTrash">
            {formatByteSize(file.size)}
            <IconTrash
              onClick={() => {
                const next = [...localFiles];
                next.splice(i, 1);
                setLocalFiles(next);
              }}
              styleName="iconButton"
            />
          </div>
        </div>,
      );
    }
  }

  return (
    <div styleName="container">
      <div
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const newFiles = [...localFiles];
          for (const file of e.dataTransfer.files) {
            const allowed = isAllowedFile(file, [
              'AUDIO',
              'DOCUMENT',
              'IMAGE',
              // 'VIDEO',
            ]);

            if (allowed.value) newFiles.push(file);
            else {
              // eslint-disable-next-line no-alert
              alert(allowed.reason);
              return;
            }
          }
          setLocalFiles(newFiles);
        }}
        styleName="dropArea"
      >
        <IconUpload />
        <p styleName="p1">Media Files</p>
        <p styleName="p2">
          Or &zwnj;
          <Link
            keepScrollPosition
            onClick={() => {
              fileInputRef.current?.click();
            }}
            to=""
          >
            click here
          </Link>
          &zwnj; to search the file in your computer
        </p>
        <input
          accept={ALLOWED_ASSET_EXT.join(',')}
          multiple
          onChange={(e) => {
            if (e.target.files) {
              for (const file of e.target.files) {
                const allowed = isAllowedFile(file, [
                  'AUDIO',
                  'DOCUMENT',
                  'IMAGE',
                  // 'VIDEO',
                ]);
                if (!allowed.value) {
                  // eslint-disable-next-line no-alert
                  alert(allowed.reason);
                  return;
                }
              }

              // TODO: Well, ideally we should compare new files with already
              // added ones (via hashing, I guess) and disallow adding the same
              // file multiple times. Perhaps, not something to spend extra time
              // on right now.
              setLocalFiles([...localFiles, ...e.target.files]);

              // This is necessary, as if we have selected a file, then removed
              // it, then selected it again there won't be a change detected, if
              // we have not reset the input state since the previous selection.
              if (fileInputRef.current) fileInputRef.current.value = '';
            }
          }}
          ref={fileInputRef}
          styleName="hidden"
          type="file"
        />
      </div>
      <div styleName="fileFormatAndSizeDetails">
        <WithTooltip
          tip={(
            <div styleName="fileFormatAndSizeTip">
              <div><strong>Video:</strong> MP4, MOV, AVI (max 500MB)</div>
              <div><strong>Audio:</strong> MP3, WAV, M4A (max 100MB)</div>
              <div><strong>Document:</strong> PDF, DOC, DOCX (max 50MB)</div>
              <div><strong>Image:</strong> JPG, PNG, HEIC (max 25MB)</div>
            </div>
          )}
        >
          <IconInfo />
        </WithTooltip> File format and size details.
      </div>
      {fileNodes}
      {/* TODO: Well... not yet, implement it later.
      <p>You can also provide a link for your files</p>
      <Input />
      */}
    </div>
  );
};
